// v49.2 — Profile menu dropdown.
// Ports the v48.181 sidebar profile dropdown to React + Tailwind.
// Click avatar → menu floats above with Account / (hybrid only) Switch
// role / Sign out. Click outside or Escape closes.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";

const HYBRID_ROLE_KEY = "lc_hybrid_active_role_v1";

export function ProfileMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  /* Hybrid users can flip between coordinator + partner side. The active
     side is mirrored to localStorage so v48.x components that read it
     during the migration window stay in sync. v49.2 just stores the
     value; downstream feature slices read it when they need to branch. */
  const initialHybridSide = (() => {
    try { return localStorage.getItem(HYBRID_ROLE_KEY) || "coordinator"; }
    catch { return "coordinator"; }
  })();
  const [hybridSide, setHybridSide] = useState<"coordinator" | "partner">(
    initialHybridSide === "partner" ? "partner" : "coordinator",
  );
  const flipHybridSide = (side: "coordinator" | "partner") => {
    setHybridSide(side);
    try { localStorage.setItem(HYBRID_ROLE_KEY, side); } catch { /* ignore */ }
    /* Notify any feature module subscribed to the hybrid switch. */
    try { window.dispatchEvent(new CustomEvent("lc-hybrid-side-changed", { detail: { side } })); } catch { /* ignore */ }
  };

  /* Outside-click + Escape close. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const isHybrid = user.role === "hybrid";

  return (
    <div className="relative" ref={ref}>
      {/* Avatar trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lc-md transition-colors ${
          open ? "bg-lc-bg-2 ring-1 ring-lc-line" : "hover:bg-lc-bg-2"
        }`}
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-lc-navy to-lc-blue-dark text-white font-extrabold text-xs flex items-center justify-center shrink-0">
          {user.initials}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-xs font-bold text-lc-ink truncate">{user.name}</div>
          <div className="text-[10px] font-extrabold uppercase tracking-wide text-lc-faint">
            {user.role}
            {isHybrid && <span className="ml-1 text-lc-blue">· {hybridSide}</span>}
            <span className="ml-1 text-lc-blue normal-case tracking-normal">· View profile</span>
          </div>
        </div>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-lc-faint)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Floating menu */}
      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 bottom-full mb-2 bg-white border border-lc-line rounded-lc-lg shadow-xl p-1 z-50"
        >
          <MenuItem
            label="View profile"
            onClick={() => { setOpen(false); navigate("/me"); }}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-lc-blue-dark)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
          />
          <MenuItem
            label="Account settings"
            onClick={() => { setOpen(false); navigate("/settings"); }}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-lc-mute)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            )}
          />

          {isHybrid && (
            <>
              <div className="my-1 mx-2 border-t border-lc-line" />
              <div className="px-3 pt-2 pb-1.5 text-[10px] font-extrabold tracking-widest uppercase text-lc-faint">
                Hybrid side
              </div>
              <div className="grid grid-cols-2 gap-1 px-1 pb-1">
                <SideToggle
                  label="Coordinator"
                  active={hybridSide === "coordinator"}
                  onClick={() => flipHybridSide("coordinator")}
                />
                <SideToggle
                  label="Partner"
                  active={hybridSide === "partner"}
                  onClick={() => flipHybridSide("partner")}
                />
              </div>
            </>
          )}

          <div className="my-1 mx-2 border-t border-lc-line" />

          <MenuItem
            label="Sign out"
            danger
            onClick={() => { setOpen(false); signOut(); }}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            )}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label, onClick, danger, icon,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lc-md text-xs font-bold transition-colors text-left ${
        danger
          ? "text-lc-red-fg hover:bg-lc-red-bg"
          : "text-lc-ink-2 hover:bg-lc-bg-2"
      }`}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span>{label}</span>
    </button>
  );
}

function SideToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1.5 rounded-lc-sm text-[11px] font-bold transition-colors ${
        active
          ? "bg-lc-blue-light text-lc-blue-dark border border-lc-blue-border"
          : "bg-white text-lc-mute border border-lc-line hover:border-lc-faint"
      }`}
    >
      {label}
    </button>
  );
}
